import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonFunctionsService, DialogType } from './common-functions.service';
import { LogService } from './log.service';
import { AuthService } from './auth.service';
import {
  NotificationType,
  PushNotificationContainer,
  SilentActions,
} from './definitions.service';
import { DataModelService } from './data-model.service';
import { Router } from '@angular/router';
import { UniversalDialogDialogData } from '../universal-dialog/universal-dialog';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  currentMessage = new BehaviorSubject<PushNotificationContainer | null>(null);
  isRequestingToken = new BehaviorSubject<boolean>(false);
  unreadCount$: Observable<number> = of(0);

  /** Emits whenever a foreground TestMessage (type 99) is received.
   *  Subscribe in AppComponent to handle test/debug actions. */
  readonly testMessage$ = new Subject<PushNotificationContainer>();
//TODO:

  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private commonFunctions: CommonFunctionsService,
    private log: LogService,
    private authenticationService: AuthService,
    private dataModel: DataModelService,
    public router: Router
  ) {}

  /** Call once after login to wire up the unread-count observable. */
  public initInbox(userId: string) {
    this.unreadCount$ = this.dataModel
      .subscribeToInboxNotifications(userId)
      .pipe(map(notifications => notifications.filter(n => !n.read).length));
  }

  //Starts asking if user wants to receive push notification
  public requestPermission(force: boolean = false) {
    if (this.authenticationService.isLoggedIn) {
      this.requestNotificationPermission(
        this.authenticationService.userDetails.uid,
        force
      );
    } else {
      this.commonFunctions.showErrorToast('User nicht eingeloggt');
    }
  }

  receiveMessage() {
    // Foreground messages (app is open): the notification is already saved to
    // Firestore by the sender script, so no saving is needed here. For silent
    // actions the app still needs to act immediately.
    this.angularFireMessaging.messages.subscribe(
      (payload: any) => {
        const p = payload as PushNotificationContainer;
        console.log('Message received. ', p);
        this.currentMessage.next(p);
        this.handleIncomingPayload(p);
      }
    );

    // Background silent actions are broadcast by the service worker so the app
    // can execute them even when the push arrived while the tab was hidden.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const msg = event.data;
        if (!msg || !msg.payload) return;
        if (msg.type === 'BACKGROUND_MESSAGE_RECEIVED') {
          const payload = msg.payload as PushNotificationContainer;
          const messageType = parseInt(payload?.data?.messageType ?? '', 10) || 0;
          if (messageType === NotificationType.SilentAction) {
            this.handleSilentActions(parseInt(payload.data?.silentActionId ?? '', 10));
          }
        }
      });
    }
  }

  /**
   * Handles a received foreground FCM payload.
   * The sender script already saved the notification to Firestore, so the
   * live inbox subscription will update automatically.
   */
  private handleIncomingPayload(payload: PushNotificationContainer) {
    const data = payload?.data || {};
    const messageType = parseInt(data.messageType ?? '', 10) || 0;

    if (messageType === NotificationType.SilentAction) {
      this.handleSilentActions(parseInt(data.silentActionId ?? '', 10));
      return;
    }

//TODO:

    if (messageType === NotificationType.TestMessage) {
      // Emit for AppComponent (or any subscriber) to handle in-app.
      this.testMessage$.next(payload);
      return;
    }

    // Navigate to the notification's own route when set, otherwise to the inbox.
    const route = data.route ? ('/' + data.route) : '/notifications';
    const queryParams: Record<string, string> = {};
    if (data.notificationDocId) queryParams['id'] = data.notificationDocId;
    if (messageType) queryParams['messageType'] = String(messageType);

    this.router.navigate([route], { queryParams });
  }

  //Handles silent actions without any user notification
  private handleSilentActions(silentActionId: number) {
    if (silentActionId === SilentActions.SendLogFilesToDev) {
      this.log
        .sendLogsToDev(this.authenticationService.userId)
        .then(
          () => console.log('Logs erfolgreich versandt'),
          (rejectMsg) => console.log('Logs nicht versandt: ' + rejectMsg)
        );
    }
    if (silentActionId === SilentActions.LogOut) {
      this.authenticationService.logout();
    }
    if (silentActionId === SilentActions.ClearLogFiles) {
      this.log.clearLogs();
    }
  }

  /**
   * Requests permission to retrieve web notifications
   * Source: https://github.com/angular/angularfire/blob/master/docs/messaging/messaging.md
   */
  public requestNotificationPermission(userId: string, force: boolean = false) {
    //already requested?
    var alreadyRequested = localStorage.getItem('notiTokenRequested');
    if (alreadyRequested == null || force) {
      this.commonFunctions
        .openDialog(
          'Benachrichtigungen',
          'Möchtest du Push-Benachrichtigungen erhalten?',
          DialogType.INFO,
          'Nein',
          'Ja'
        )
        .afterClosed()
        .subscribe((result: UniversalDialogDialogData) => {
          localStorage.setItem('notiTokenRequested', 'true');

          if (result.result == 1) {
            this.isRequestingToken.next(true);
            this.angularFireMessaging.requestToken.subscribe(
              (token) => {
                this.isRequestingToken.next(false);
                this.log.debug(
                  'Permission for receiving notifications granted. Sending token to server',
                  token,
                  CommonFunctionsService.name
                );
                this.commonFunctions.saveNotiTokenInUserCollection(
                  userId,
                  token ?? ''
                );
              },
              (error) => {
                this.isRequestingToken.next(false);
                this.commonFunctions.showErrorToast(
                  'Registrierung nicht möglich'
                );
                this.log.error(
                  'Request for receiving notification token failed',
                  error,
                  CommonFunctionsService.name
                );
              }
            );
          }
        });
    }
  }
}
